// =============================================================================
// Confirmar ou trocar de linha — a decisão sobre a sugestão do motor
//
// O motor casa por centro de custo + categoria + competência (§3.5). Quando
// há mais de uma linha nessa chave e o fornecedor não desempata, ele SUGERE
// pela descrição, e sugestão por texto nunca se confirma sozinha. Esta tela é
// onde o Financeiro decide.
//
// As opções são só as linhas da mesma chave (migration 036). Mover o gasto
// para outra categoria ou outro centro de custo não é decidir a sugestão, é
// reclassificar o lançamento — e isso é no Conta Azul, que é a origem.
//
// Confirmar ou trocar fecha a pendência "sugestão a confirmar" no mesmo passo,
// e as duas ações ficam no Histórico — de onde um admin pode desfazê-las.
//
// Migration 040: a mesma decisão pode valer para os "iguais" — outros
// lançamentos do mesmo fornecedor, categoria e CC esperando decisão — e virar
// regra para os próximos meses. Uma assinatura mensal deixa de pedir nove
// cliques por ano.
// =============================================================================

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buscarCandidatos,
  confirmarAlocacao,
  FalhaApi,
  realocarAlocacao,
  type Aplicacao,
  type CandidatosAlocacao,
} from "../lib/api.ts";
import { competenciaCurta, moeda } from "../lib/formato.ts";
import { Modal } from "../componentes/Modal.tsx";
import { Pilula, Vazio } from "../componentes/ui.tsx";

const NOVA_DESPESA_NAO_ORCADA = "__nova_despesa_nao_orcada__";

export function ModalAlocacao({
  lancamentoId,
  onFechar,
  onDecidido,
}: {
  lancamentoId: string;
  onFechar: () => void;
  onDecidido?: () => void;
}) {
  const q = useQuery({
    queryKey: ["candidatos", lancamentoId],
    queryFn: () => buscarCandidatos(lancamentoId),
  });

  return (
    <Modal titulo="Confirmar ou trocar de linha" onFechar={onFechar} largura="max-w-2xl">
      {q.isPending && <p className="py-8 text-center text-[13px] text-tinta-fraca">Carregando…</p>}
      {q.isError && <Vazio titulo="Não foi possível carregar as opções" dica={(q.error as FalhaApi).message} />}
      {q.data && <Decisao dados={q.data} onFechar={onFechar} onDecidido={onDecidido} />}
    </Modal>
  );
}

function Decisao({
  dados,
  onFechar,
  onDecidido,
}: {
  dados: CandidatosAlocacao;
  onFechar: () => void;
  onDecidido?: () => void;
}) {
  const qc = useQueryClient();
  const { lancamento: l, atual, candidatos } = dados;
  const iguais = dados.iguais ?? [];
  const [linhaId, setLinhaId] = useState<string | null>(atual?.linha_id ?? candidatos[0]?.linha_id ?? null);
  // Uma alocação confirmada sem despesa_orcada_id, numa linha que tem
  // subcategorias, representa a decisão de registrar uma despesa nova.
  // Sugestões sem subcategoria continuam exigindo uma escolha explícita.
  const escolhaInicial = (id: string | null) => {
    if (id !== atual?.linha_id) return "";
    if (atual.despesa_orcada_id) return atual.despesa_orcada_id;
    return atual.estado === "confirmada" && candidatos.find((c) => c.linha_id === id)?.subcategorias.length
      ? NOVA_DESPESA_NAO_ORCADA : "";
  };
  const [subEscolha, setSubEscolha] = useState(() => escolhaInicial(linhaId));
  const subId = subEscolha && subEscolha !== NOVA_DESPESA_NAO_ORCADA ? subEscolha : null;
  const novaDespesaNaoOrcada = subEscolha === NOVA_DESPESA_NAO_ORCADA;
  const [motivo, setMotivo] = useState("");
  // Marcações feitas à mão sobre o padrão sugerido, por lançamento. Zeram quando
  // a subcategoria muda, porque o que é aplicável muda junto.
  const [marcas, setMarcas] = useState<{ sub: string | null; ids: Record<string, boolean> }>({ sub: null, ids: {} });
  const [lembrarMarcado, setLembrarMarcado] = useState<{ sub: string | null; valor: boolean } | null>(null);
  const [resultado, setResultado] = useState<Aplicacao>(null);

  const escolhida = candidatos.find((c) => c.linha_id === linhaId);
  const mesmaLinha = !!atual && atual.linha_id === linhaId;
  const mesmaSub = (atual?.despesa_orcada_id ?? null) === subId;
  // Confirmar: aceitar a sugestão como está, ou só escolher a subcategoria
  // dentro da linha sugerida. Qualquer outra coisa é troca, e troca exige motivo.
  const acao: "confirmar" | "trocar" | "nada" =
    !linhaId ? "nada"
    : !atual ? "confirmar"
    : atual.estado === "sugerida" && mesmaLinha && (mesmaSub || subId !== null) ? "confirmar"
    : mesmaLinha && mesmaSub ? "nada"
    : "trocar";
  const precisaSub = (escolhida?.subcategorias.length ?? 0) > 0 && !subEscolha;

  // ── os iguais ──────────────────────────────────────────────────────────────
  const nomeSub = escolhida?.subcategorias.find((s) => s.id === subId)?.nome ?? null;
  const chaveNome = (n: string) => n.trim().toLowerCase();
  const aplicavel = (i: (typeof iguais)[number]) =>
    !!nomeSub && i.subcategorias.some((n) => chaveNome(n) === chaveNome(nomeSub));
  const marcado = (i: (typeof iguais)[number]) =>
    aplicavel(i) && (marcas.sub === nomeSub && i.lancamento_id in marcas.ids
      ? marcas.ids[i.lancamento_id]!
      : i.mesma_descricao); // o padrão: marca a mesma cobrança, não o resto do fornecedor
  const marcar = (id: string, valor: boolean) =>
    setMarcas((m) => ({ sub: nomeSub, ids: { ...(m.sub === nomeSub ? m.ids : {}), [id]: valor } }));
  const aplicaveis = iguais.filter(aplicavel);
  const aplicarA = iguais.filter(marcado).map((i) => i.lancamento_id);

  // Lembrar vem ligado quando parece cobrança recorrente: há a mesma descrição
  // em outros meses, e nunca duas no mesmo mês. Duas no mesmo mês ("Pedido #"
  // do Mercado Livre) é compra avulsa — lembrar mandaria toda compra futura para
  // a mesma subcategoria, então vem desligado e a pessoa liga se quiser.
  const lembrarPadrao = useMemo(() => {
    const meses = [l.competencia, ...iguais.filter((i) => i.mesma_descricao).map((i) => i.competencia)];
    return meses.length > 1 && new Set(meses).size === meses.length;
  }, [iguais, l.competencia]);
  const podeLembrar = !!nomeSub && !!l.padrao;
  const lembrar = podeLembrar && (lembrarMarcado?.sub === nomeSub ? lembrarMarcado.valor : lembrarPadrao);
  const mostraIguais = acao !== "nada" && !!nomeSub && (iguais.length > 0 || podeLembrar);

  const decidir = useMutation({
    mutationFn: () => {
      const extra = mostraIguais ? { aplicar_a: aplicarA, lembrar } : {};
      return acao === "confirmar"
        ? confirmarAlocacao({ lancamento_id: l.id, linha_id: linhaId!, despesa_orcada_id: subId, ...extra })
        : realocarAlocacao({ lancamento_id: l.id, linha_id: linhaId!, despesa_orcada_id: subId, motivo: motivo.trim(), ...extra });
    },
    onSuccess: async (r) => {
      // Mexe em pendência, alocação e realizado por linha: tudo o que está em tela.
      await qc.invalidateQueries();
      onDecidido?.();
      // Com aplicação, o resultado fica na tela: quem aplicou precisa saber o
      // que ficou de fora e por quê.
      if (r.aplicacao) setResultado(r.aplicacao);
      else onFechar();
    },
  });

  if (resultado) return <ResultadoAplicacao r={resultado} iguais={iguais} onFechar={onFechar} />;

  const pode =
    acao !== "nada" && !precisaSub && !decidir.isPending && (acao === "confirmar" || motivo.trim().length >= 5);

  return (
    <div className="grid gap-4">
      {/* ── o lançamento ─────────────────────────────────────────────────── */}
      <section className="rounded-lg border border-linha bg-superficie-2 px-4 py-3">
        <p className="text-[14.5px] font-bold">{l.descricao || "— sem descrição —"}</p>
        <p className="mt-1 text-[12.5px] text-tinta-media">
          {[l.fornecedor, l.categoria, competenciaCurta(l.competencia)].filter(Boolean).join(" · ")}
        </p>
        <p className="num mt-1 text-[13px]">
          <strong className="font-semibold">{moeda(l.valor_centavos)}</strong>
          {l.centros_custo.length > 0 && (
            <span className="text-tinta-media">
              {" · "}
              {l.centros_custo.map((c) => `${c.codigo} ${c.nome}`).join(", ")}
            </span>
          )}
        </p>
      </section>

      {atual?.estado === "confirmada" && (
        <p className="rounded-lg border border-ok-linha bg-ok-bg px-3 py-2 text-[12.5px] text-ok-tinta">
          Este lançamento já está confirmado numa linha. Escolher outra linha é uma troca, e pede motivo.
        </p>
      )}

      {/* ── as linhas possíveis ──────────────────────────────────────────── */}
      {candidatos.length === 0 ? (
        <Vazio
          titulo="Nenhuma linha orçada na mesma chave"
          dica="Não há linha com este centro de custo, esta categoria e esta competência. Cadastre a linha que falta, ou resolva a pendência na Central explicando."
        />
      ) : (
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-[12px] font-semibold text-tinta-media">
            Linhas com o mesmo centro de custo, categoria e competência
          </legend>
          {candidatos.map((c) => {
            const marcada = c.linha_id === linhaId;
            const sugerida = atual?.linha_id === c.linha_id;
            return (
              <label key={c.linha_id}
                className={`cursor-pointer rounded-lg border px-3 py-2.5 ${marcada ? "border-laranja bg-superficie" : "border-linha"}`}>
                <span className="flex flex-wrap items-center gap-2">
                  <input type="radio" name="linha" checked={marcada}
                    onChange={() => { setLinhaId(c.linha_id); setSubEscolha(escolhaInicial(c.linha_id)); }} />
                  <span className="text-[13.5px] font-semibold">{c.centro_custo}</span>
                  <span className="text-[12.5px] text-tinta-media">· {competenciaCurta(c.competencia)}</span>
                  <span className="num text-[12.5px] text-tinta-media">· orçado {moeda(c.valor_centavos)}</span>
                  {sugerida && (
                    <Pilula tom={atual?.estado === "confirmada" ? "ok" : "atencao"}>
                      {atual?.estado === "confirmada" ? "confirmada" : "sugerida pelo motor"}
                    </Pilula>
                  )}
                  {c.tem_fornecedor_igual && <Pilula tom="ok">mesmo fornecedor</Pilula>}
                </span>
                {c.fornecedor && <span className="mt-0.5 block pl-6 text-[12px] text-tinta-fraca">fornecedor da linha: {c.fornecedor}</span>}
                {/* Duas linhas na mesma chave se parecem; o que as distingue
                    costuma ser o que foi orçado dentro de cada uma. */}
                {!marcada && c.subcategorias.length > 0 && (
                  <span className="mt-0.5 block pl-6 text-[12px] text-tinta-fraca">
                    subcategorias: {c.subcategorias.map((s) => s.nome).join(", ")}
                  </span>
                )}

                {marcada && c.subcategorias.length > 0 && (
                  <span className="mt-2 block pl-6">
                    <span className="text-[12px] font-semibold text-tinta-media">Subcategoria</span>
                    <select value={subEscolha} onChange={(e) => setSubEscolha(e.target.value)}
                      className="mt-1 block w-full rounded-lg border border-linha bg-superficie px-2 py-1.5 text-[13px]">
                      <option value="">— escolha —</option>
                      <option value={NOVA_DESPESA_NAO_ORCADA}>Nova despesa não orçada</option>
                      {c.subcategorias.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.nome} · {moeda(s.valor_centavos)}
                          {s.similaridade !== null ? ` · ${Math.round(s.similaridade * 100)}% parecido com a descrição` : ""}
                        </option>
                      ))}
                    </select>
                    {novaDespesaNaoOrcada && (
                      <span className="mt-1 block text-[11.5px] text-tinta-media">
                        O gasto fica nesta categoria, sem vínculo com uma subcategoria orçada. O orçamento não muda.
                      </span>
                    )}
                  </span>
                )}
              </label>
            );
          })}
        </fieldset>
      )}

      {acao === "trocar" && (
        <label className="grid gap-1">
          <span className="text-[12px] font-semibold text-tinta-media">
            {novaDespesaNaoOrcada ? "Motivo da classificação" : "Motivo da troca"} <span className="text-laranja-tinta">*</span>
          </span>
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500}
            placeholder={novaDespesaNaoOrcada ? "Por que esta despesa não estava orçada?" : "Por que este lançamento é desta linha e não da sugerida."}
            className="rounded-lg border border-linha bg-superficie px-3 py-2 text-[13.5px]" />
          <span className="text-[11.5px] text-tinta-fraca">Fica no Histórico de Movimentações.</span>
        </label>
      )}

      {precisaSub && <p className="text-[12px] text-atencao-tinta">Escolha uma subcategoria ou “Nova despesa não orçada”.</p>}

      {/* ── aplicar aos iguais (migration 040) ────────────────────────────── */}
      {mostraIguais && (
        <section className="rounded border border-linha px-4 py-3">
          {iguais.length > 0 && (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-[13px] font-bold">
                  Aplicar “{nomeSub}” também a estes
                </h3>
                {aplicaveis.length > 0 && (
                  <span className="flex gap-3 text-[12px]">
                    <button type="button" className="font-semibold text-laranja-tinta hover:underline"
                      onClick={() => setMarcas({ sub: nomeSub, ids: Object.fromEntries(aplicaveis.map((i) => [i.lancamento_id, true])) })}>
                      marcar todos
                    </button>
                    <button type="button" className="text-tinta-fraca hover:underline"
                      onClick={() => setMarcas({ sub: nomeSub, ids: Object.fromEntries(aplicaveis.map((i) => [i.lancamento_id, false])) })}>
                      desmarcar
                    </button>
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-[12px] text-tinta-media">
                Outros lançamentos de {l.fornecedor ?? "sem fornecedor"} nesta categoria e centro de custo que ainda
                esperam decisão. Cada um vai para a subcategoria de mesmo nome no próprio mês. Vêm marcados os de mesma
                descrição; confira os outros — o mesmo fornecedor pode vender coisas diferentes.
              </p>
              <ul className="thin-scroll mt-2 grid max-h-56 gap-1 overflow-y-auto pr-1">
                {iguais.map((i) => {
                  const pode = aplicavel(i);
                  return (
                    <li key={i.lancamento_id}>
                      <label className={`flex items-start gap-2 rounded px-2 py-1 text-[12.5px] ${pode ? "cursor-pointer hover:bg-superficie-2" : "opacity-55"}`}>
                        <input type="checkbox" className="mt-0.5" disabled={!pode} checked={marcado(i)}
                          onChange={(e) => marcar(i.lancamento_id, e.target.checked)} />
                        <span className="min-w-0 flex-1">
                          <span className="font-medium">{i.descricao || "— sem descrição —"}</span>
                          <span className="num text-tinta-media"> · {competenciaCurta(i.competencia)} · {moeda(i.valor_centavos)}</span>
                          {i.mesma_descricao && <span className="ml-1.5 text-[11px] font-semibold text-ok-tinta">mesma descrição</span>}
                          {!pode && (
                            <span className="block text-[11.5px] text-tinta-fraca">
                              {competenciaCurta(i.competencia)} não tem a subcategoria “{nomeSub}”
                            </span>
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {podeLembrar && (
            <label className={`flex cursor-pointer items-start gap-2 ${iguais.length > 0 ? "mt-3 border-t border-linha pt-3" : ""}`}>
              <input type="checkbox" className="mt-1" checked={lembrar}
                onChange={(e) => setLembrarMarcado({ sub: nomeSub, valor: e.target.checked })} />
              <span className="text-[12.5px]">
                <span className="font-semibold">Lembrar para os próximos meses.</span>{" "}
                Os próximos lançamentos de {l.fornecedor ?? "sem fornecedor"} com a descrição “{l.padrao}” entram
                sozinhos em “{nomeSub}”, já confirmados.
                <span className="mt-0.5 block text-[11.5px] text-tinta-fraca">
                  Números e nomes de mês são ignorados na comparação (# é qualquer número). Descrição diferente continua
                  chegando como sugestão. Um admin desfaz pelo Histórico.
                </span>
              </span>
            </label>
          )}
        </section>
      )}

      {decidir.isError && (
        <p className="rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
          {(decidir.error as FalhaApi).message}
        </p>
      )}

      <div className="flex justify-end gap-2 border-t border-linha pt-3">
        <button type="button" onClick={onFechar}
          className="rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold hover:bg-superficie-2">
          Cancelar
        </button>
        <button type="button" disabled={!pode} onClick={() => decidir.mutate()}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          {decidir.isPending ? "Registrando…"
            : novaDespesaNaoOrcada && acao !== "nada" ? "Registrar despesa não orçada"
            : acao === "trocar" ? "Trocar para esta linha"
            : acao === "confirmar" ? "Confirmar"
            : "Nada a mudar"}
          {!decidir.isPending && acao !== "nada" && mostraIguais && aplicarA.length > 0 && ` e aplicar a ${aplicarA.length}`}
        </button>
      </div>
    </div>
  );
}

/** O que a aplicação fez — e, principalmente, o que ficou de fora e por quê. */
function ResultadoAplicacao({
  r,
  iguais,
  onFechar,
}: {
  r: NonNullable<Aplicacao>;
  iguais: CandidatosAlocacao["iguais"];
  onFechar: () => void;
}) {
  const descricao = (id: string) => {
    const i = iguais.find((x) => x.lancamento_id === id);
    return i ? `${i.descricao || "— sem descrição —"} · ${competenciaCurta(i.competencia)}` : "lançamento";
  };
  return (
    <div className="grid gap-3">
      <p className="rounded border border-ok-linha bg-ok-bg px-3 py-2 text-[13px] text-ok-tinta">
        Confirmado.
        {r.aplicadas > 0 && <> A mesma subcategoria foi aplicada a <strong>{r.aplicadas}</strong> {r.aplicadas === 1 ? "lançamento" : "lançamentos"}, e as pendências deles saíram da fila.</>}
        {r.regras > 0 && <> Os próximos meses entram sozinhos.</>}
      </p>
      {r.ignoradas.length > 0 && (
        <div>
          <p className="text-[12.5px] font-semibold text-tinta-media">
            {r.ignoradas.length} {r.ignoradas.length === 1 ? "ficou" : "ficaram"} de fora:
          </p>
          <ul className="thin-scroll mt-1 grid max-h-48 gap-1 overflow-y-auto text-[12.5px]">
            {r.ignoradas.map((x) => (
              <li key={x.lancamento_id}>
                <span className="font-medium">{descricao(x.lancamento_id)}</span>
                <span className="text-tinta-media"> — {x.motivo}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex justify-end border-t border-linha pt-3">
        <button type="button" onClick={onFechar}
          className="rounded bg-laranja px-4 py-2 text-[13px] font-bold text-azul">
          Fechar
        </button>
      </div>
    </div>
  );
}
